import { DuplicateApnDialog, duplicateFromError } from "../components/DuplicateApnDialog";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Sprout, Play, Loader2, MapPin, Landmark, DollarSign, Ruler, Building2,
  CheckCircle2, AlertTriangle, Plug, RefreshCw, ShieldQuestion, Send,
  ShieldCheck, ShieldAlert, Map as MapIcon, Rocket, CalendarClock, Save,
} from "lucide-react";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";

const money = (v) =>
  v == null || v === "" ? "—" : `$${Number(v).toLocaleString()}`;
const acresLabel = (c) =>
  c.acreage != null ? `${c.acreage} ac` : c.lot_sqft != null ? `${Number(c.lot_sqft).toLocaleString()} sf` : "—";

const SourceChip = ({ s }) => {
  const ok = s.connected;
  const color = ok
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-slate-100 text-slate-500 border-slate-200";
  return (
    <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs ${color}`}
         data-testid={`datasource-${s.name.toLowerCase().replace(/\s+/g, "-")}`}>
      <Plug className="h-3.5 w-3.5" />
      <div>
        <div className="font-semibold">{s.name}</div>
        <div className="opacity-80">{s.purpose} · {s.status}</div>
      </div>
    </div>
  );
};

const Funnel = ({ f }) => {
  const items = [
    ["Raw Candidates", f.raw_candidates],
    ["Land Qualified", f.land_qualified],
    ["Unique Parcels", f.unique_parcels],
    ["GIS Verified", f.gis_verified],
    ["Angela Analyzed", f.angela_analyzed],
    ["Property Leads", f.property_leads_created],
  ];
  return (
    <div className="grid grid-cols-3 md:grid-cols-6 gap-3" data-testid="run-funnel">
      {items.map(([label, val]) => (
        <div key={label} className="rounded-lg border border-slate-200 bg-white p-3 text-center">
          <div className="text-2xl font-bold text-slate-900">{val ?? 0}</div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mt-1">{label}</div>
        </div>
      ))}
    </div>
  );
};

export default function PropertyCandidates() {
  const [buyboxes, setBuyboxes] = useState([]);
  const [sources, setSources] = useState([]);
  const [runs, setRuns] = useState([]);
  const [activeRun, setActiveRun] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [running, setRunning] = useState(false);
  const [testing, setTesting] = useState(false);
  const [promoting, setPromoting] = useState(null);
  const [dupInfo, setDupInfo] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [pipeline, setPipeline] = useState(false);
  const [pipelineStage, setPipelineStage] = useState(null);
  const [scheduleFor, setScheduleFor] = useState(null);
  const [sched, setSched] = useState(null);

  const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const STAGE_LABEL = {
    queued: "Queued…", sourcing: "Sourcing land from providers…",
    gis_verify: "Verifying parcels against county GIS…",
    angela: "Angela analyzing the best parcels…",
    promoting: "Promoting verified parcels to Leads…",
    done: "Pipeline complete", error: "Pipeline failed",
  };

  const openSchedule = (bb) => {
    if (scheduleFor === bb.id) { setScheduleFor(null); return; }
    setScheduleFor(bb.id);
    const s = bb.schedule || {};
    setSched({
      enabled: !!s.enabled, frequency: s.frequency || "daily",
      weekdays: s.weekdays || [], time: s.time || "05:00",
      timezone: s.timezone || "America/Los_Angeles",
      top_n: bb.pipeline_top_n || 10,
    });
  };

  const toggleWeekday = (i) => setSched((p) => ({
    ...p, weekdays: p.weekdays.includes(i) ? p.weekdays.filter((x) => x !== i) : [...p.weekdays, i],
  }));

  const saveSchedule = async (bbId) => {
    try {
      const r = await api.put(`/candidates/buyboxes/${bbId}/schedule`, {
        schedule: {
          enabled: sched.enabled, frequency: sched.frequency,
          weekdays: sched.weekdays, time: sched.time, timezone: sched.timezone,
        },
        top_n: Number(sched.top_n) || 10,
      });
      toast.success(sched.enabled
        ? `Schedule saved — next run ${r.data.next_run_at?.slice(0, 16).replace("T", " ")} UTC`
        : "Schedule disabled");
      setScheduleFor(null);
      await loadMeta();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const runPipeline = async (bbId) => {
    setPipeline(true);
    setPipelineStage("queued");
    toast.info("Full pipeline started — sourcing, GIS verify, Angela, auto-promote…");
    try {
      const r = await api.post("/candidates/pipeline/run", { buybox_id: bbId });
      const runId = r.data.run_id;
      for (let i = 0; i < 240; i++) {
        await new Promise((res) => setTimeout(res, 5000));
        const rr = await api.get(`/candidates/runs/${runId}`);
        setActiveRun(rr.data);
        setCandidates(rr.data.candidates || []);
        const st = rr.data.pipeline_status;
        setPipelineStage(st);
        if (st === "done") {
          const s = rr.data.pipeline_summary || {};
          toast.success(`Pipeline complete — ${s.gis_verified || 0} GIS-verified, ${s.angela_analyzed || 0} analyzed, ${s.promoted || 0} promoted to Leads`);
          await loadMeta();
          break;
        }
        if (st === "error") { toast.error("Pipeline failed — check backend logs"); break; }
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPipeline(false);
      setPipelineStage(null);
    }
  };

  const analyzeBest = async () => {
    if (!activeRun) return;
    setAnalyzing(true);
    toast.info("Angela is analyzing the best GIS-verified parcels…");
    try {
      const r = await api.post(`/candidates/runs/${activeRun.id}/analyze-best`, { top_n: 10 });
      const runId = activeRun.id;
      for (let i = 0; i < 90; i++) {
        await new Promise((res) => setTimeout(res, 5000));
        const rr = await api.get(`/candidates/runs/${runId}`);
        setActiveRun(rr.data);
        setCandidates(rr.data.candidates || []);
        const st = rr.data.angela_status;
        if (st === "done") {
          const s = rr.data.angela_summary || {};
          toast.success(`Angela analyzed ${s.analyzed || 0} of ${s.requested || r.data.queued} parcels`);
          break;
        }
        if (st === "error") { toast.error("Angela analysis failed"); break; }
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setAnalyzing(false);
    }
  };
  const navigate = useNavigate();

  const verifyRun = async () => {
    if (!activeRun) return;
    setVerifying(true);
    toast.info("Verifying parcels against local county GIS…");
    try {
      await api.post(`/candidates/runs/${activeRun.id}/verify`);
      // Background job — poll the run until GIS verification completes.
      const runId = activeRun.id;
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const rr = await api.get(`/candidates/runs/${runId}`);
        setActiveRun(rr.data);
        setCandidates(rr.data.candidates || []);
        const st = rr.data.gis_verify_status;
        if (st === "done") {
          const s = rr.data.gis_verify_summary || {};
          toast.success(`GIS check complete — ${s.verified || 0} verified, ${s.conflicts || 0} APN conflict(s) flagged`);
          break;
        }
        if (st === "error") { toast.error("GIS verification failed"); break; }
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setVerifying(false);
    }
  };

  const promote = async (c, allowDuplicate = false) => {
    if (c.promoted_lead_id) { navigate(`/record/inbound_leads/${c.promoted_lead_id}`); return; }
    setPromoting(c.id);
    try {
      const r = await api.post(`/candidates/${c.id}/promote`, null, { params: { allow_duplicate: allowDuplicate } });
      setDupInfo(null);
      toast.success("Promoted to Lead — enrichment agents running");
      setCandidates((prev) => prev.map((x) => x.id === c.id ? { ...x, promoted_lead_id: r.data.lead_id, qualification_status: "Promoted to Lead" } : x));
      navigate(`/record/inbound_leads/${r.data.lead_id}`);
    } catch (e) {
      const d = duplicateFromError(e);
      if (d) setDupInfo({ ...d, cand: c });
      else toast.error(apiError(e));
    } finally {
      setPromoting(null);
    }
  };

  const loadMeta = async () => {
    try {
      const [bb, ds, rn] = await Promise.all([
        api.get("/candidates/buyboxes"),
        api.get("/candidates/datasources/status"),
        api.get("/candidates/runs"),
      ]);
      setBuyboxes(bb.data.buyboxes || []);
      setSources(ds.data.sources || []);
      setRuns(rn.data.runs || []);
      if (!activeRun && (rn.data.runs || []).length) {
        openRun((rn.data.runs || [])[0].id);
      }
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => { loadMeta(); /* eslint-disable-next-line */ }, []);

  const openRun = async (runId) => {
    try {
      const r = await api.get(`/candidates/runs/${runId}`);
      setActiveRun(r.data);
      setCandidates(r.data.candidates || []);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const runBuyBox = async (bbId) => {
    setRunning(true);
    toast.info("Sourcing active land from RealEstateAPI…");
    try {
      const r = await api.post("/candidates/run", { buybox_id: bbId });
      toast.success(`Run complete — ${r.data.candidate_count ?? 0} unique land parcels`);
      await loadMeta();
      await openRun(r.data.id);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRunning(false);
    }
  };

  const testConnection = async (which = "realestateapi") => {
    setTesting(true);
    try {
      const r = await api.post(`/candidates/datasources/${which}/test`);
      r.data.ok ? toast.success(r.data.message) : toast.error(r.data.error);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="property-candidates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-emerald-600">AI Command Center</div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2 mt-1">
            <Sprout className="h-7 w-7 text-emerald-600" /> Property Candidates
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Real land parcels sourced directly from professional data providers — no broad AI guessing.
            Candidates are staged here before GIS verification and Angela development analysis.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => testConnection("realestateapi")} disabled={testing} data-testid="test-realestateapi-btn">
            {testing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plug className="h-4 w-4 mr-2" />}
            Test RealEstateAPI
          </Button>
          <Button variant="outline" onClick={() => testConnection("propertyradar")} disabled={testing} data-testid="test-propertyradar-btn">
            {testing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plug className="h-4 w-4 mr-2" />}
            Test PropertyRadar
          </Button>
        </div>
      </div>

      {/* Data sources */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="datasource-strip">
        {sources.map((s) => <SourceChip key={s.name} s={s} />)}
      </div>

      {/* Buy boxes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {buyboxes.map((bb) => (
          <Card key={bb.id} className="p-5" data-testid={`buybox-${bb.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-semibold text-slate-900">{bb.name}</h3>
                <div className="text-sm text-slate-500 mt-1 flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" /> {bb.state} · {(bb.counties || []).join(", ")}
                </div>
                <div className="flex flex-wrap gap-2 mt-3 text-xs">
                  <Badge variant="secondary">Type: {bb.property_type}</Badge>
                  {bb.max_price && <Badge variant="secondary">≤ {money(bb.max_price)}</Badge>}
                  {bb.min_lot_sqft && <Badge variant="secondary">≥ {Number(bb.min_lot_sqft).toLocaleString()} sf</Badge>}
                  {bb.vacant_preferred && <Badge variant="secondary">Vacant preferred</Badge>}
                  {bb.schedule?.enabled && (
                    <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200" data-testid={`schedule-badge-${bb.id}`}>
                      <CalendarClock className="h-3 w-3 mr-1" />
                      {bb.schedule.frequency === "weekly"
                        ? `Weekly ${(bb.schedule.weekdays || []).map((i) => WEEKDAYS[i]).join("/")}`
                        : "Daily"} {bb.schedule.time}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <Button onClick={() => runPipeline(bb.id)} disabled={pipeline || running}
                        data-testid={`run-pipeline-${bb.id}`}>
                  {pipeline ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Rocket className="h-4 w-4 mr-2" />}
                  Run Full Pipeline
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => runBuyBox(bb.id)} disabled={running || pipeline}
                          data-testid={`run-buybox-${bb.id}`}>
                    {running ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Play className="h-4 w-4 mr-1" />}
                    Source only
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openSchedule(bb)}
                          data-testid={`schedule-btn-${bb.id}`}>
                    <CalendarClock className="h-4 w-4 mr-1" /> Schedule
                  </Button>
                </div>
              </div>
            </div>

            {pipeline && pipelineStage && (
              <div className="mt-4 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700"
                   data-testid={`pipeline-stage-${bb.id}`}>
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> {STAGE_LABEL[pipelineStage] || pipelineStage}
              </div>
            )}

            {scheduleFor === bb.id && sched && (
              <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3"
                   data-testid={`schedule-editor-${bb.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-700">Automated Pipeline Schedule</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">Enabled</span>
                    <Switch checked={sched.enabled} onCheckedChange={(v) => setSched((p) => ({ ...p, enabled: v }))}
                            data-testid={`schedule-enabled-${bb.id}`} />
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <select value={sched.frequency} onChange={(e) => setSched((p) => ({ ...p, frequency: e.target.value }))}
                          className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm"
                          data-testid={`schedule-frequency-${bb.id}`}>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                  </select>
                  <Input type="time" value={sched.time} onChange={(e) => setSched((p) => ({ ...p, time: e.target.value }))}
                         className="w-28" data-testid={`schedule-time-${bb.id}`} />
                  <select value={sched.timezone} onChange={(e) => setSched((p) => ({ ...p, timezone: e.target.value }))}
                          className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm">
                    <option value="America/Los_Angeles">Pacific</option>
                    <option value="America/Denver">Mountain</option>
                    <option value="America/Chicago">Central</option>
                    <option value="America/New_York">Eastern</option>
                    <option value="UTC">UTC</option>
                  </select>
                </div>
                {sched.frequency === "weekly" && (
                  <div className="flex flex-wrap gap-1.5">
                    {WEEKDAYS.map((d, i) => (
                      <button key={d} onClick={() => toggleWeekday(i)}
                              className={`rounded-md border px-2.5 py-1 text-xs ${sched.weekdays.includes(i) ? "border-emerald-300 bg-emerald-100 text-emerald-700" : "border-slate-200 bg-white text-slate-500"}`}
                              data-testid={`schedule-weekday-${bb.id}-${i}`}>{d}</button>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <span>Analyze best</span>
                  <Input type="number" min={1} max={50} value={sched.top_n}
                         onChange={(e) => setSched((p) => ({ ...p, top_n: e.target.value }))}
                         className="w-20" data-testid={`schedule-topn-${bb.id}`} />
                  <span>parcels with Angela · then auto-promote GIS-verified parcels to Leads (APN conflicts skipped)</span>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setScheduleFor(null)}>Cancel</Button>
                  <Button size="sm" onClick={() => saveSchedule(bb.id)} data-testid={`schedule-save-${bb.id}`}>
                    <Save className="h-4 w-4 mr-1" /> Save schedule
                  </Button>
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* Active run summary */}
      {activeRun && (
        <Card className="p-5 space-y-4" data-testid="active-run">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className="font-semibold text-slate-900">{activeRun.buy_box_name} — latest run</h3>
              <div className="text-xs text-slate-500">
                {activeRun.started_at?.slice(0, 19).replace("T", " ")} · {activeRun.trigger}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {activeRun.status === "failed"
                ? <Badge className="bg-red-100 text-red-700 border-red-200"><AlertTriangle className="h-3.5 w-3.5 mr-1" />Failed</Badge>
                : <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200"><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Completed</Badge>}
              <span className="text-sm text-slate-500">
                Data cost: {money(activeRun.cost?.total)}
              </span>
            </div>
          </div>

          {activeRun.funnel && <Funnel f={activeRun.funnel} />}

          {/* Source health — one block per source */}
          {activeRun.source_health && Object.entries(activeRun.source_health).map(([name, h]) => h && (
            <div key={name} className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid={`source-health-${name.toLowerCase()}`}>
              <div className={`text-xs font-semibold uppercase tracking-wide mb-2 ${h.status === "FAILED" ? "text-red-600" : "text-slate-600"}`}>
                {name} — {h.status}
                {" · "}{h.calls} calls
                {" · "}{h.records_returned} records
              </div>
              <ul className="text-xs text-slate-600 space-y-1">
                {(h.notes || []).map((n, i) => (
                  <li key={i} className="flex gap-2">
                    <span className={n.includes("FAILED") ? "text-red-400" : "text-slate-400"}>•</span>{n}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </Card>
      )}

      {/* Candidates table */}
      <Card className="p-0 overflow-hidden" data-testid="candidates-table">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Land Candidates {candidates.length ? `(${candidates.length})` : ""}</h3>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={verifyRun} disabled={verifying || !activeRun} data-testid="verify-gis-btn">
              {verifying ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1" />} Verify GIS
            </Button>
            <Button variant="outline" size="sm" onClick={analyzeBest} disabled={analyzing || !activeRun} data-testid="analyze-best-btn">
              {analyzing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sprout className="h-4 w-4 mr-1" />} Analyze Best 10
            </Button>
            <Button variant="ghost" size="sm" onClick={() => activeRun && openRun(activeRun.id)}>
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">Channel</th>
                <th className="text-left px-4 py-3">APN</th>
                <th className="text-left px-4 py-3">Address</th>
                <th className="text-left px-4 py-3">County</th>
                <th className="text-right px-4 py-3">Asking</th>
                <th className="text-right px-4 py-3">Lot</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">DOM</th>
                <th className="text-left px-4 py-3">Owner</th>
                <th className="text-left px-4 py-3">Source</th>
                <th className="text-right px-4 py-3">Dev Score</th>
                <th className="text-right px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map((c) => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`candidate-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <Badge className="bg-blue-50 text-blue-700 border-blue-200">{c.channel}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-xs">{c.apn || "—"}</span>
                      {c.parcel_geometry && <MapIcon className="h-3 w-3 text-emerald-600" title="Parcel boundary saved" />}
                    </div>
                    {c.apn_verification_status === "Verified" && (
                      <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-600" data-testid={`apn-verified-${c.id}`}>
                        <ShieldCheck className="h-3 w-3" />Verified{c.gis_source ? ` · ${c.gis_source.split("(")[0].trim()}` : ""}
                      </span>
                    )}
                    {c.apn_verification_status === "Conflict" && (
                      <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-red-600" title={c.apn_conflict_note} data-testid={`apn-conflict-${c.id}`}>
                        <ShieldAlert className="h-3 w-3" />NEEDS REVIEW
                      </span>
                    )}
                    {(!c.apn_verification_status || c.apn_verification_status === "Preliminary") && c.apn && (
                      <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-600">
                        <ShieldQuestion className="h-3 w-3" />Preliminary
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">{c.address || c.address_full || "—"}<div className="text-xs text-slate-400">{c.city}, {c.state} {c.zip}</div></td>
                  <td className="px-4 py-3">{c.county || "—"}</td>
                  <td className="px-4 py-3 text-right">{money(c.asking_price)}</td>
                  <td className="px-4 py-3 text-right">{acresLabel(c)}</td>
                  <td className="px-4 py-3">
                    <Badge variant="secondary">{c.listing_status || "—"}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right">{c.days_on_market ?? "—"}</td>
                  <td className="px-4 py-3">
                    {c.owner_name || "—"}
                    {c.absentee_owner && <div className="text-[10px] text-emerald-600">Absentee</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{c.source}</td>
                  <td className="px-4 py-3 text-right" data-testid={`dev-score-${c.id}`}>
                    {c.development_opportunity_score != null ? (
                      <span className={`inline-flex items-center justify-center rounded-md px-2 py-0.5 text-xs font-bold ${c.development_opportunity_score >= 70 ? "bg-emerald-100 text-emerald-700" : c.development_opportunity_score >= 45 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}
                            title={(c.development_analysis || {}).summary || ""}>
                        {c.development_opportunity_score}
                      </span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {c.promoted_lead_id ? (
                      <Button variant="ghost" size="sm" onClick={() => promote(c)} data-testid={`view-lead-${c.id}`}>
                        <CheckCircle2 className="h-4 w-4 mr-1 text-emerald-600" /> Lead
                      </Button>
                    ) : (
                      <Button size="sm" onClick={() => promote(c)} disabled={promoting === c.id} data-testid={`promote-${c.id}`}>
                        {promoting === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Promote</>}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {!candidates.length && (
                <tr><td colSpan={12} className="px-4 py-10 text-center text-slate-400">
                  No candidates yet. Run a Buy Box above to source real active land parcels.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Recent runs */}
      {runs.length > 1 && (
        <Card className="p-5">
          <h3 className="font-semibold text-slate-900 mb-3">Recent Runs</h3>
          <div className="space-y-2">
            {runs.map((r) => (
              <button key={r.id} onClick={() => openRun(r.id)}
                      className={`w-full flex items-center justify-between text-left rounded-lg border px-4 py-2 text-sm transition-colors ${activeRun?.id === r.id ? "border-emerald-300 bg-emerald-50" : "border-slate-200 hover:bg-slate-50"}`}
                      data-testid={`run-item-${r.id}`}>
                <span>{r.buy_box_name} · {r.started_at?.slice(0, 19).replace("T", " ")}</span>
                <span className="text-slate-500">{r.candidate_count ?? 0} parcels</span>
              </button>
            ))}
          </div>
        </Card>
      )}
      <DuplicateApnDialog dup={dupInfo} busy={!!promoting} onClose={() => setDupInfo(null)}
        onCreateAnyway={() => promote(dupInfo.cand, true)} />
    </div>
  );
}
